// /admin/audit — agent_audit_log read-only timeline. Every tool call by every
// inbox agent (cold-intake, approval, inbox-thread). Useful for debugging
// "why didn't X fire" or "which action did the agent take".

import { Card, CardContent } from "@/components/ui/card";
import { formatDateGB } from "@/lib/format";
import { getSupabase } from "@/lib/supabase";
import { useEffect, useState } from "react";

type AuditRow = {
  id: string;
  agent_name: string;
  action_name: string;
  repair_id: string | null;
  pending_action_id: string | null;
  source_message_id: string | null;
  args: unknown;
  result: unknown;
  success: boolean;
  error_message: string | null;
  duration_ms: number | null;
  created_at: string;
};

export function AdminAudit() {
  const [rows, setRows] = useState<AuditRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [agentFilter, setAgentFilter] = useState<string | "all">("all");
  const [successFilter, setSuccessFilter] = useState<"all" | "errors">("all");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      const sb = getSupabase();
      let q = sb
        .from("agent_audit_log")
        .select(
          "id, agent_name, action_name, repair_id, pending_action_id, source_message_id, args, result, success, error_message, duration_ms, created_at",
        )
        .order("created_at", { ascending: false })
        .limit(200);
      if (agentFilter !== "all") q = q.eq("agent_name", agentFilter);
      if (successFilter === "errors") q = q.eq("success", false);
      const { data, error: fetchErr } = await q;
      if (cancelled) return;
      if (fetchErr) setError(fetchErr.message);
      else setRows((data ?? []) as AuditRow[]);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [agentFilter, successFilter]);

  const agents = ["all", "cold-intake-agent", "approval-agent", "inbox-agent", "agents"] as const;

  return (
    <div className="flex flex-col gap-4 max-w-5xl mx-auto w-full">
      <div>
        <h1 className="text-2xl md:text-3xl font-semibold tracking-tight">Agent audit log</h1>
        <p className="text-sm text-ink-dim mt-1">
          Read-only stream of every inbox-agent tool call. Latest 200 rows.
        </p>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs text-ink-dim">Agent:</span>
        {agents.map((a) => (
          <FilterPill key={a} active={agentFilter === a} onClick={() => setAgentFilter(a)}>
            {a === "all" ? "All" : a}
          </FilterPill>
        ))}
        <span className="text-xs text-ink-dim ml-3">Show:</span>
        <FilterPill active={successFilter === "all"} onClick={() => setSuccessFilter("all")}>
          Everything
        </FilterPill>
        <FilterPill active={successFilter === "errors"} onClick={() => setSuccessFilter("errors")}>
          Errors only
        </FilterPill>
      </div>

      {error ? (
        <p role="alert" className="text-xs text-red-300">
          {error}
        </p>
      ) : null}

      {loading ? (
        <p className="text-sm text-ink-dim">Loading…</p>
      ) : rows.length === 0 ? (
        <Card>
          <CardContent>
            <p className="text-sm text-ink-dim py-6 text-center">No audit entries match.</p>
          </CardContent>
        </Card>
      ) : (
        <ul className="flex flex-col gap-2">
          {rows.map((r) => (
            <li key={r.id}>
              <Card>
                <CardContent>
                  <div className="flex items-center gap-2 flex-wrap text-xs">
                    <code className="px-1.5 py-0.5 rounded-md border border-line bg-bg-card text-ink-dim">
                      {r.agent_name}
                    </code>
                    <code className="px-1.5 py-0.5 rounded-md border border-line bg-bg-card text-ink">
                      {r.action_name}
                    </code>
                    {r.success ? (
                      <span className="inline-flex items-center text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-md border bg-emerald-500/15 text-emerald-300 border-emerald-500/30">
                        ok
                      </span>
                    ) : (
                      <span className="inline-flex items-center text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-md border bg-red-500/15 text-red-300 border-red-500/30">
                        fail
                      </span>
                    )}
                    {r.duration_ms != null ? (
                      <span className="text-ink-dim">{r.duration_ms}ms</span>
                    ) : null}
                    <span className="ml-auto text-ink-dim">{formatDateGB(r.created_at)}</span>
                  </div>
                  {r.error_message ? (
                    <p className="text-xs text-red-300 mt-1">{r.error_message}</p>
                  ) : null}
                  {r.args || r.result ? (
                    <details className="text-xs mt-2">
                      <summary className="cursor-pointer text-ink-dim hover:text-ink">
                        Args / result
                      </summary>
                      <pre className="mt-2 p-2 rounded-md bg-bg-card border border-line overflow-x-auto text-[11px] leading-snug">
                        {JSON.stringify({ args: r.args, result: r.result }, null, 2)}
                      </pre>
                    </details>
                  ) : null}
                </CardContent>
              </Card>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function FilterPill({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`text-xs px-2.5 py-1 rounded-full border transition ${
        active
          ? "bg-accent text-accent-on border-accent"
          : "bg-bg-card text-ink-dim border-line hover:text-ink"
      }`}
    >
      {children}
    </button>
  );
}
